import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import { villaData } from '../data/mockData';
import { useToast } from '../hooks/use-toast';

export const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    checkIn: '',
    checkOut: '',
    guests: '',
    message: ''
  });
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `Salam! Rezervasiya sorğusu:

Ad: ${formData.name}
Telefon: ${formData.phone}
Email: ${formData.email}
Giri\u015f: ${formData.checkIn}
\u00c7\u0131x\u0131\u015f: ${formData.checkOut}
Qonaq say\u0131: ${formData.guests}
Mesaj: ${formData.message}`;
    
    // Open WhatsApp
    window.open(`https://wa.me/${villaData.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
    
    // Show success toast
    toast({
      title: "U\u011furla g\u00f6nd\u0259rildi!",
      description: "WhatsApp-da mesaj\u0131n\u0131z\u0131 g\u00f6r\u0259c\u0259ksiniz",
    });
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      checkIn: '',
      checkOut: '',
      guests: '',
      message: ''
    });
  };
  
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${villaData.whatsapp.replace(/[^0-9]/g, '')}?text=Salam, villa haqq\u0131nda m\u0259lumat almaq ist\u0259yir\u0259m`, '_blank');
  };
  
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Biziml\u0259 \u018flaq\u0259
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Suallar\u0131n\u0131z\u0131 cavabland\u0131rmaqdan m\u0259mnuniyyetle
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Cards */}
            <Card className="border-2 border-green-100">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Telefon</h3>
                    <a href={`tel:${villaData.phone}`} className="text-green-600 hover:text-green-700 font-medium">
                      {villaData.phone}
                    </a>
                    <p className="text-sm text-gray-600 mt-1">H\u0259r g\u00fcn 09:00-22:00</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-100">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                    <button
                      onClick={handleWhatsApp}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Mesaj g\u00f6nd\u0259r
                    </button>
                    <p className="text-sm text-gray-600 mt-1">S\u00fcr\u0259tli cavab</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-green-100">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">\u00dcnvan</h3>
                    <p className="text-gray-700">{villaData.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Owner Info */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-100">
              <h3 className="font-semibold text-gray-900 mb-2">\u018fmlak Sahibi</h3>
              <p className="text-lg text-gray-700">{villaData.owner}</p>
              <p className="text-sm text-gray-600 mt-2">Sizin rahat\u0131l\u0131\u011f\u0131n\u0131z bizim \u00fc\u00e7\u00fcn \u0259n vacibdir</p>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Rezervasiya Sor\u011fusu
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Ad Soyad *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Ad\u0131n\u0131z\u0131 daxil edin"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+994xx xxx xx xx"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="checkIn">Giri\u015f Tarixi *</Label>
                      <Input
                        id="checkIn"
                        name="checkIn"
                        type="date"
                        value={formData.checkIn}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="checkOut">\u00c7\u0131x\u0131\u015f Tarixi *</Label>
                      <Input
                        id="checkOut"
                        name="checkOut"
                        type="date"
                        value={formData.checkOut}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="guests">Qonaq Say\u0131 *</Label>
                      <Input
                        id="guests"
                        name="guests"
                        type="number"
                        min="1"
                        max="10"
                        value={formData.guests}
                        onChange={handleChange}
                        required
                        placeholder="2"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Mesaj</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="\u018flav\u0259 m\u0259lumat v\u0259 ya xah\u0259\u015fl\u0259rinizi yaz\u0131n..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    WhatsApp il\u0259 G\u00f6nd\u0259r
                  </Button>
                  
                  <p className="text-sm text-gray-600 text-center">
                    * Bu formu doldurduqdan sonra WhatsApp-a y\u00f6nl\u0259ndirm\u0259 al\u0131r\u0131s\u0131n\u0131z
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};